import React, {
  HTMLAttributes,
  ReactEventHandler,
  RefAttributes,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  DataFileModel,
  DataFileTypeModel,
  VehicleFileDetailsViewModel,
  VehicleFileModel,
  VehicleFileValidationTypeModel,
  VehicleTypeModel,
  getDataFileTypeLabel,
  getVehicleTypeString,
  vehicleFileAPI,
} from "../api/vehicleFileAPI";
import { useAuth } from "react-oidc-context";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Image,
  ImageProps,
  InputGroup,
  Modal,
  Nav,
  Row,
  Spinner,
  Tab,
} from "react-bootstrap";

import DataFileUpload from "./DataFileUpload";
import ErrorDisplay from "../common/ErrorDisplay";
import LoadingDisplay from "../common/LoadingDisplay";
import { Problem, Result } from "../api/backendApi";
import DataFiles from "./DataFiles";
import DataFilesDirectoryView from "./DataFilesDirectoryView";
import ModelInfosPage from "./ModelInfos";
import { useRoutablePages } from "../common/RoutablePages";
import { DataFileEntry } from "../common/fileUtils";
import ComponentEditor from "./components/ComponentEditor";
import Checkout from "./Checkout";
import { DrawingTypeModel } from "../api/drawingApi";
import DrawingEdit from "../drawings/DrawingEdit";
import { Fullscreen } from "react-bootstrap-icons";

enum Tabs {
  Files = "files",
  ModelInfo = "modelInfo",
  Components = "components",
  Drawing = "drawing",
}

enum SelectedFileType {
  SelectedFile,
  SourceFile,
}

type SelectedVehicleFile = {
  selectedFileType: SelectedFileType | null;
  vehicleFileNumber: string;
  fullPath: string;
  fileType: DataFileTypeModel;
};

export type YearlyReviewDetails = {
  makeCode: string;
  currentYear: number;
  newYearEnd: number;
  reviewed: boolean;
}

function VehicleFileDetailsPage() {
  const auth = useAuth();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [locationState, setLocationState] = useState<YearlyReviewDetails | undefined>(undefined);
  const [vehicleFile, setVehicleFile] =
    useState<VehicleFileDetailsViewModel | null>(null);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const params = useParams();
  const number = params.number ?? "";
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [uploadDataFileType, setUploadDataFileType] = useState(
    DataFileTypeModel.CrsFile,
  );
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadFilename, setUploadFilename] = useState<string | null>(null);
  const [description, setDescription] = useState<null | string>();
  const [type, setType] = useState<null | VehicleTypeModel>();
  const [rsa, setRsa] = useState<boolean>(false);

  const [crsFiles, setCrsFiles] = useState<DataFileEntry[]>([]);
  const [selectedFilesCrsPic, setSelectedFilesCrsPic] = useState<
    DataFileEntry[]
  >([]);
  const [selectedFilesTechInfo, setSelectedFilesTechInfo] = useState<
    DataFileEntry[]
  >([]);
  const [autocadFiles, setAutocadFiles] = useState<DataFileEntry[]>([]);
  const [pdfFiles, setPdfFiles] = useState<DataFileEntry[]>([]);
  const [sourceFilesOrgPic, setSourceFilesOrgPic] = useState<DataFileEntry[]>(
    [],
  );
  const [sourceFilesTechInfoEU, setSourceFilesTechInfoEU] = useState<
    DataFileEntry[]
  >([]);
  const [sourceFilesTechInfoUSA, setSourceFilesTechInfoUSA] = useState<
    DataFileEntry[]
  >([]);
  const [sourceFilesTechInfoPacific, setSourceFilesTechInfoPacific] = useState<
    DataFileEntry[]
  >([]);
  const [sourceFilesArchive, setSourceFilesArchive] = useState<DataFileEntry[]>(
    [],
  );
  const [threeDFiles, setThreeDFiles] = useState<DataFileEntry[]>([]);
  const [qrCodeFiles, setQRCodeFiles] = useState<DataFileEntry[]>([]);
  const [oemRescueSheetFiles, setOEMRescueSheetFiles] = useState<
    DataFileEntry[]
  >([]);
  const [isMpfzModified, setIsMpfzModified] = useState(false);
  const [isModelInfoModified, setIsModelInfoModified] = useState(false);
  const [isComponentsModified, setIsComponentsModified] = useState(false);

  const [showCheckInPopup, setShowCheckInPopup] = useState(false);
  const [checkInMessage, setCheckInMessage] = useState("");
  const [forceCheckIn, setForceCheckIn] = useState(false);
  const [checkInError, setCheckInError] = useState<Problem | undefined>(
    undefined,
  );

  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [exportCounter, setExportCounter] = useState(0);
  const [selectedDataFileEntry, setSelectedDataFileEntry] =
    useState<SelectedVehicleFile | null>(null);
  let initialTabParameter = searchParams.get("tab");
  let initialTab = Tabs.Files;

  if (
    initialTabParameter &&
    Object.values(Tabs).indexOf(initialTabParameter as Tabs) >= 0
  ) {
    initialTab = initialTabParameter as Tabs;
  }

  const routes = useRoutablePages();

  const refreshDetails = useCallback(() => {
    setLoading(true);
    setLocationState(location.state);

    vehicleFileAPI
      .getVehicleFileDetails(number)
      .then((result) => {
        if (result.ok) {
          setVehicleFile(result.value);
          setDescription(result.value?.description);
          setType(result.value?.type);
          setRsa(result.value?.rsa ?? false);

          setCrsFiles(parseDataFiles(result.value?.crsFiles));
          setSelectedFilesCrsPic(
            parseDataFiles(result.value?.selectedFilesCrsPic),
          );
          setSelectedFilesTechInfo(
            parseDataFiles(result.value?.selectedFilesTechInfo),
          );
          setAutocadFiles(parseDataFiles(result.value?.autocadFiles));
          setPdfFiles(parseDataFiles(result.value?.pdfFiles));
          setSourceFilesOrgPic(parseDataFiles(result.value?.sourceFilesOrgPic));
          setSourceFilesTechInfoEU(
            parseDataFiles(result.value?.sourceFilesTechInfoEU),
          );
          setSourceFilesTechInfoUSA(
            parseDataFiles(result.value?.sourceFilesTechInfoUSA),
          );
          setSourceFilesTechInfoPacific(
            parseDataFiles(result.value?.sourceFilesTechInfoPacific),
          );
          setSourceFilesArchive(
            parseDataFiles(result.value?.sourceFilesArchive),
          );
          setThreeDFiles(parseDataFiles(result.value?.threeDFiles));
          setQRCodeFiles(parseDataFiles(result.value?.qrCodeFiles));
          setOEMRescueSheetFiles(
            parseDataFiles(result.value?.oemRescueSheetFiles),
          );
        }
        setLoading(false);
      })
      .catch((e) => {
        setError(e);
        setLoading(false);
      });
    // added exportCount here to force a refresh when it changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [number, exportCounter]);

  const startCheckIn = async (force: boolean) => {
    var hasChanges = await vehicleFileAPI.checkForChanges(number);

    if (hasChanges.ok) {
      setCheckInError(undefined);
      setHasChanges(hasChanges.value);
      setForceCheckIn(force);

      if (hasChanges.value) {
        if (locationState !== undefined && locationState !== null && locationState.newYearEnd !== null) {
          setCheckInMessage(`Yearly review for ${locationState.currentYear}`);
        } else {
          setCheckInMessage("");
        }
      }

      setShowCheckInPopup(true);
    } else {
      setError(hasChanges.problem);
    }
  };

  const onCheckIn = async () => {
    setError(undefined);
    let result: Result<any>;

    if (hasChanges)
      result = await vehicleFileAPI.checkinVehicleFile(
        number,
        checkInMessage,
        forceCheckIn,
      );
    else result = await vehicleFileAPI.undoCheckout(number, forceCheckIn);

    if (locationState !== undefined && locationState !== null && locationState.newYearEnd !== null) {
      routes.toModelInfoToReview(
        locationState.makeCode,
        locationState.currentYear,
      );
    }

    if (result.ok) {
      setShowCheckInPopup(false);
      await refreshDetails();

      if (locationState?.reviewed === true) {
        await vehicleFileAPI.setVehicleValidatedFlag(
          number,
          VehicleFileValidationTypeModel.ModelInfo,
          true,
        );
        await vehicleFileAPI.setVehicleValidatedFlag(
          number,
          VehicleFileValidationTypeModel.HelperFilesValidation,
          true,
        );
      }
    } else {
      setCheckInError(result.problem);
    }
  };

  const checkout = async () => {
    setError(undefined);
    const result = await vehicleFileAPI.checkoutVehicleFile(number);

    if (result.ok) {
      await refreshDetails();
    }
  };

  const addFile = (path: string, type: DataFileTypeModel) => {
    setUploadFilename(null);
    setUploadTitle(path);
    setUploadDataFileType(type);
    setShowUploadPopup(true);
  };

  const updateFile = (file: DataFileModel | undefined) => {
    if (!file) return;
    setUploadDataFileType(file.type);
    setUploadTitle("");
    setUploadFilename(file.filename);
    setShowUploadPopup(true);
  };

  const deleteFile = (path: string, type: DataFileTypeModel) => {
    vehicleFileAPI
      .removeDataFile(number, path, type)
      .then((result) => {
        if (result.ok) {
          refreshDetails();
        } else {
          setError(result.problem);
        }
      })
      .catch((e) => {
        setError(e);
      });
  };

  const downloadFile = (
    fullPath: string | undefined,
    type: DataFileTypeModel,
  ) => {
    vehicleFileAPI
      .downloadDataFile(number, fullPath ?? "", type, false)
      .catch((e) => {
        setError(e);
      });
  };

  const viewFile = (fullPath: string | undefined, type: DataFileTypeModel) => {
    if (!fullPath) return;

    vehicleFileAPI.downloadDataFile(number, fullPath, type, true).catch((e) => {
      setError(e);
    });
  };

  const viewFileViewInline =
    (selectedFileType: SelectedFileType) =>
    (fullPath: string | undefined, type: DataFileTypeModel) => {
      if (!fullPath) return;
      if (
        fullPath.endsWith(".png") ||
        fullPath.endsWith(".jpg") ||
        fullPath.endsWith(".jpeg") ||
        fullPath.endsWith(".pdf")
      ) {
        setSelectedDataFileEntry({
          selectedFileType,
          vehicleFileNumber: vehicleFile?.number!,
          fileType: type,
          fullPath,
        });
        return;
      }
      setSelectedDataFileEntry(null);
    };

  useEffect(() => {
    // this works because it's a callback, otherwise it'd be looping
    refreshDetails();
  }, [refreshDetails]);

  const handleDescriptionChange = (event: any) => {
    setDescription(event.target.value);
  };

  const handleTypeChange = (event: any) => {
    const value = Number(event.target.value);

    if (!isNaN(value)) setType(value);
  };

  const handleRsaChange = (event: any) => {
    setRsa(event.target.checked ?? false);
  };

  const updateVehicleDetails = () => {
    setError(undefined);
    vehicleFileAPI
      .updateVehicleFile(number, {
        description: description,
        type: type,
        rsa: rsa,
      } as VehicleFileModel)
      .then((result) => {
        if (result.ok) {
          refreshDetails();
        } else {
          setError(result.problem);
        }
      })
      .catch((e) => {
        setError(e);
      });
  };

  const toggleFullscreen = () => {
    setFullscreen((fs) => !fs);
  };

  const handleDeselectDataFileEntry: ReactEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    setSelectedDataFileEntry(null);
  };

  const fullscreenStyle: React.CSSProperties = {
    backgroundColor: "#f3f4f6", // taken from #main-wrapper
    position: "fixed",
    inset: "0.5rem",
    zIndex: 10,
    height: "auto",
  };

  const [fullscreen, setFullscreen] = useState(false);

  return (
    <>
      <div className="main-container-title">
        <Row style={{ margin: "0 0.5em 0.5em 0.5em" }}>
          <Col>
            <h1>Vehicle File: {number}</h1>
          </Col>
          {!loading && (
            <>
              <Col md="auto" className="align-content-center">
                <Checkout
                  checkedOutBy={vehicleFile?.checkedOutBy ?? undefined}
                  unsavedChangesDrawing={isMpfzModified}
                  unsavedChangesModelInfo={isModelInfoModified}
                  unsavedChangesComponents={isComponentsModified}
                  onCheckin={startCheckIn}
                  onCheckout={checkout}
                />
              </Col>
              <Col md="auto" className="align-content-center">
                <a href={routes.getVehicleFileHistory(number)}>view history</a>
              </Col>
            </>
          )}
        </Row>
        <ErrorDisplay error={error} />
        <LoadingDisplay loading={loading} />
        <InputGroup size="sm" className="my-3 mb-0">
          <InputGroup.Text>Type</InputGroup.Text>
          <Form.Select
            value={type as number}
            onChange={handleTypeChange}
            className="me-auto"
            style={{ flex: "0 content" }}
          >
            {!type && (
              <option key="0" value="">
                [select]
              </option>
            )}
            {Object.keys(VehicleTypeModel)
              .filter((key) => !isNaN(Number(key)))
              .map((key) => (
                <option key={key} value={Number(key)}>
                  {getVehicleTypeString(Number(key))}
                </option>
              ))}
          </Form.Select>
          <InputGroup.Checkbox checked={rsa} onChange={handleRsaChange} />
          <InputGroup.Text
            style={{ borderLeft: 0, cursor: "pointer" }}
            onClick={() => setRsa(!rsa)}
          >
            RSA
          </InputGroup.Text>
          <InputGroup.Text>Description</InputGroup.Text>
          <Form.Control
            type="text"
            value={description ?? ""}
            onChange={handleDescriptionChange}
          />
          <Button
            disabled={
              loading ||
              ((description ?? "") === (vehicleFile?.description ?? "") &&
                (type ?? "") === (vehicleFile?.type ?? "") &&
                (rsa ?? false) === (vehicleFile?.rsa ?? false))
            }
            onClick={() => updateVehicleDetails()}
          >
            Save
          </Button>
        </InputGroup>
        <Form.Text className="mb-3">
          Fill the "description" with the following items: Model Name, Body
          Style, (Extra model info), Model Code, Start year – Year end
        </Form.Text>
        {!loading && (
          <Container fluid className="mt-3 px-0">
            <Row className="gx-0"></Row>
          </Container>
        )}
      </div>
      <div
        className="main-container-content"
        style={fullscreen ? fullscreenStyle : {}}
      >
        <Tab.Container
          id="vehicle-file-tab"
          defaultActiveKey={initialTab}
          onSelect={(k) => k && setSearchParams({ ...searchParams, tab: k })}
        >
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey={Tabs.Files}>Files</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={Tabs.ModelInfo}>Model Info</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={Tabs.Drawing}>Drawing</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey={Tabs.Components}>Components</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey=""
                onClick={(e) => {
                  toggleFullscreen();
                  e.stopPropagation();
                }}
                style={{ border: "0" }}
                title="Toggle tab full screen display"
              >
                <Fullscreen size="16" />
              </Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content className="h-100">
            <Tab.Pane eventKey={Tabs.Files} className="h-100">
              <Card className="mb-3">
                <Card.Header>
                  {getDataFileTypeLabel(DataFileTypeModel.CrsFile)}
                  <small className="text-body-secondary ms-3">
                    Data file (.mpfz)
                  </small>
                </Card.Header>
                <Card.Body>
                  <DataFiles
                    files={crsFiles}
                    multiple={false}
                    disabled={
                      vehicleFile?.checkedOutBy?.email !==
                      auth.user?.profile.email
                    }
                    onView={(path) => viewFile(path, DataFileTypeModel.CrsFile)}
                    onDownload={(path) =>
                      downloadFile(path, DataFileTypeModel.CrsFile)
                    }
                    onDelete={(path) =>
                      deleteFile(path, DataFileTypeModel.CrsFile)
                    }
                    onUpdate={updateFile}
                    onAdd={() => addFile("", DataFileTypeModel.CrsFile)}
                  />
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header>
                  {getDataFileTypeLabel(DataFileTypeModel.AutoCADFile)}
                  <small className="text-body-secondary ms-3">
                    Drawing file (.dwg)
                  </small>
                </Card.Header>
                <Card.Body>
                  <DataFiles
                    files={autocadFiles}
                    multiple={false}
                    disabled={
                      vehicleFile?.checkedOutBy?.email !==
                      auth.user?.profile.email
                    }
                    onView={(path) =>
                      viewFile(path, DataFileTypeModel.AutoCADFile)
                    }
                    onDownload={(path) =>
                      downloadFile(path, DataFileTypeModel.AutoCADFile)
                    }
                    onDelete={(path) =>
                      deleteFile(path, DataFileTypeModel.AutoCADFile)
                    }
                    onUpdate={updateFile}
                    onAdd={() => addFile("", DataFileTypeModel.AutoCADFile)}
                  />
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header>
                  {getDataFileTypeLabel(DataFileTypeModel.PDFFile)}
                  <small className="text-body-secondary ms-3">
                    PDF files linked by the CRS File (.pdf)
                  </small>
                </Card.Header>
                <Card.Body>
                  <DataFiles
                    files={pdfFiles}
                    multiple={true}
                    disabled={
                      vehicleFile?.checkedOutBy?.email !==
                      auth.user?.profile.email
                    }
                    onView={(path) => viewFile(path, DataFileTypeModel.PDFFile)}
                    onDownload={(path) =>
                      downloadFile(path, DataFileTypeModel.PDFFile)
                    }
                    onDelete={(path) =>
                      deleteFile(path, DataFileTypeModel.PDFFile)
                    }
                    onUpdate={updateFile}
                    onAdd={() => addFile("", DataFileTypeModel.PDFFile)}
                  />
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header>
                  Selected Files
                  <small className="text-body-secondary ms-3">
                    Information used to compile the file information. CRS Pics
                    and selected documentation.
                  </small>
                </Card.Header>
                <Card.Body className="d-flex flex-column justify-content-center">
                  <DataFilesDirectoryView
                    files={[selectedFilesCrsPic, selectedFilesTechInfo]}
                    types={[
                      DataFileTypeModel.SelectedFileCrsPic,
                      DataFileTypeModel.SelectedFileCheckInfo,
                    ]}
                    disabled={
                      vehicleFile?.checkedOutBy?.email !==
                      auth.user?.profile.email
                    }
                    onView={viewFileViewInline(SelectedFileType.SelectedFile)}
                    onViewNewTab={viewFile}
                    onDownload={downloadFile}
                    onDelete={deleteFile}
                    onAdd={(path, type) => addFile(path, type)}
                  />
                  {selectedDataFileEntry?.selectedFileType ===
                  SelectedFileType.SelectedFile ? (
                    <>
                      <Button
                        variant="danger"
                        onClick={handleDeselectDataFileEntry}
                        style={{ margin: ".5em" }}
                      >
                        Close Content
                      </Button>
                      {selectedDataFileEntry.fullPath.endsWith(".pdf") ? (
                        <AsyncVehicleFilePdf
                          selectedVehicleFile={selectedDataFileEntry}
                          className="align-self-center"
                          width="100%"
                          height="1000px"
                        />
                      ) : (
                        <AsyncBlobVehicleFileImage
                          className="align-self-center"
                          selectedVehicleFile={selectedDataFileEntry}
                        />
                      )}
                    </>
                  ) : null}
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header>
                  Source Files
                  <small className="text-body-secondary ms-3">
                    OEM information to create the vehicle files (pictures and
                    technical information).
                  </small>
                </Card.Header>
                <Card.Body className="d-flex flex-column justify-content-center">
                  <DataFilesDirectoryView
                    files={[
                      sourceFilesOrgPic,
                      sourceFilesTechInfoEU,
                      sourceFilesTechInfoUSA,
                      sourceFilesTechInfoPacific,
                      sourceFilesArchive,
                    ]}
                    types={[
                      DataFileTypeModel.SourceFileOrgPic,
                      DataFileTypeModel.SourceFileTechInfoEU,
                      DataFileTypeModel.SourceFileTechInfoUSA,
                      DataFileTypeModel.SourceFileTechInfoPacific,
                      DataFileTypeModel.SourceFileArchive,
                    ]}
                    disabled={
                      vehicleFile?.checkedOutBy?.email !==
                      auth.user?.profile.email
                    }
                    onView={viewFileViewInline(SelectedFileType.SourceFile)}
                    onViewNewTab={viewFile}
                    onDownload={downloadFile}
                    onDelete={deleteFile}
                    onAdd={(path, type) => addFile(path, type)}
                  />
                  {selectedDataFileEntry?.selectedFileType ===
                  SelectedFileType.SourceFile ? (
                    <>
                      <Button
                        variant="danger"
                        onClick={handleDeselectDataFileEntry}
                        style={{ margin: ".5em" }}
                      >
                        Close Content
                      </Button>
                      {selectedDataFileEntry.fullPath.endsWith(".pdf") ? (
                        <AsyncVehicleFilePdf
                          selectedVehicleFile={selectedDataFileEntry}
                          className="align-self-center"
                          width="100%"
                          height="1000px"
                        />
                      ) : (
                        <AsyncBlobVehicleFileImage
                          className="align-self-center"
                          selectedVehicleFile={selectedDataFileEntry}
                        />
                      )}
                    </>
                  ) : null}
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header>
                  {getDataFileTypeLabel(DataFileTypeModel.ThreeDFile)}
                  <small className="text-body-secondary ms-3">
                    Mesh and material data for 3D models.
                  </small>
                </Card.Header>
                <Card.Body>
                  <DataFiles
                    files={threeDFiles}
                    multiple={true}
                    disabled={
                      vehicleFile?.checkedOutBy?.email !==
                      auth.user?.profile.email
                    }
                    onView={(path) =>
                      viewFile(path, DataFileTypeModel.ThreeDFile)
                    }
                    onDownload={(path) =>
                      downloadFile(path, DataFileTypeModel.ThreeDFile)
                    }
                    onDelete={(path) =>
                      deleteFile(path, DataFileTypeModel.ThreeDFile)
                    }
                    onUpdate={updateFile}
                    onAdd={() => addFile("", DataFileTypeModel.ThreeDFile)}
                  />
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header>
                  {getDataFileTypeLabel(DataFileTypeModel.QRCodeFile)}
                  <small className="text-body-secondary ms-3">
                    QR codes provided to our customers.
                  </small>
                </Card.Header>
                <Card.Body>
                  <DataFiles
                    files={qrCodeFiles}
                    multiple={true}
                    disabled={
                      vehicleFile?.checkedOutBy?.email !==
                      auth.user?.profile.email
                    }
                    onView={(path) =>
                      viewFile(path, DataFileTypeModel.QRCodeFile)
                    }
                    onDownload={(path) =>
                      downloadFile(path, DataFileTypeModel.QRCodeFile)
                    }
                    onDelete={(path) =>
                      deleteFile(path, DataFileTypeModel.QRCodeFile)
                    }
                    onUpdate={updateFile}
                    onAdd={() => addFile("", DataFileTypeModel.QRCodeFile)}
                  />
                </Card.Body>
              </Card>
              <Card className="">
                <Card.Header>
                  {getDataFileTypeLabel(DataFileTypeModel.OEMRescueSheetFile)}
                  <small className="text-body-secondary ms-3">
                    Rescue Sheets provided to our customers.
                  </small>
                </Card.Header>
                <Card.Body>
                  <DataFiles
                    files={oemRescueSheetFiles}
                    multiple={true}
                    disabled={
                      vehicleFile?.checkedOutBy?.email !==
                      auth.user?.profile.email
                    }
                    onView={(path) =>
                      viewFile(path, DataFileTypeModel.OEMRescueSheetFile)
                    }
                    onDownload={(path) =>
                      downloadFile(path, DataFileTypeModel.OEMRescueSheetFile)
                    }
                    onDelete={(path) =>
                      deleteFile(path, DataFileTypeModel.OEMRescueSheetFile)
                    }
                    onUpdate={updateFile}
                    onAdd={() =>
                      addFile("", DataFileTypeModel.OEMRescueSheetFile)
                    }
                  />
                </Card.Body>
              </Card>
            </Tab.Pane>
            <Tab.Pane eventKey={Tabs.ModelInfo} className="h-100">
              {vehicleFile && (
                <div
                  className="h-100"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <ModelInfosPage
                    locationState={locationState}
                    vehicleFile={vehicleFile}
                    disabled={
                      vehicleFile?.checkedOutBy?.email !==
                      auth.user?.profile.email
                    }
                    onModified={(isModified) =>
                      setIsModelInfoModified(isModified)
                    }
                    onLocationStateChange={(changedLocationState) =>
                      setLocationState(changedLocationState)}
                  />
                </div>
              )}
            </Tab.Pane>
            <Tab.Pane eventKey={Tabs.Drawing} className="h-100">
              {vehicleFile && (
                <DrawingEdit
                  drawingName={vehicleFile.number}
                  drawingType={DrawingTypeModel.Drawing}
                  readOnly={
                    vehicleFile?.checkedOutBy?.email !==
                    auth.user?.profile.email
                  }
                  onExport={() =>
                    setExportCounter((currentValue) => currentValue + 1)
                  }
                  onMpfzPublishableChange={(isModified) =>
                    setIsMpfzModified(isModified)
                  }
                />
              )}
            </Tab.Pane>
            <Tab.Pane eventKey={Tabs.Components} className="h-100">
              {vehicleFile && (
                <ComponentEditor
                  vehicleFileNumber={vehicleFile.number}
                  isModelInfoModified={isModelInfoModified}
                  readOnly={
                    vehicleFile?.checkedOutBy?.email !==
                    auth.user?.profile.email
                  }
                  hasMpfzFile={crsFiles.length > 0}
                  fileVersion={crsFiles[0]?.file?.version}
                  onModified={(isModified) =>
                    setIsComponentsModified(isModified)
                  }
                />
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>

      {showUploadPopup && (
        <DataFileUpload
          number={number}
          type={uploadDataFileType}
          title={uploadTitle}
          filename={uploadFilename}
          onOk={() => {
            setShowUploadPopup(false);
            refreshDetails();
          }}
          onCancel={() => setShowUploadPopup(false)}
        />
      )}

      <Modal show={showCheckInPopup} onHide={() => setShowCheckInPopup(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Check in vehicle file</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <ErrorDisplay error={checkInError} />
          {hasChanges ? (
            <>
              <Form.Label htmlFor="checkin-message">
                Check in message
              </Form.Label>
              <Form.Control
                type="text"
                id="checkin-message"
                defaultValue={checkInMessage}
                onChange={(e: any) => setCheckInMessage(e.target.value ?? "")}
                autoFocus
              ></Form.Control>
            </>
          ) : (
            <p>
              No changes were detected. Checking in will simply release the
              file.
            </p>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCheckInPopup(false)}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onCheckIn()}>
            Check in
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

function parseDataFiles(files: DataFileModel[] | undefined): DataFileEntry[] {
  if (!files) return [];

  let root: DataFileEntry[] = [];

  files.forEach((file) => {
    var pathSegments = file.filename.split("/");

    let currentNode: DataFileEntry = {
      name: "",
      fullPath: "",
      file: undefined,
      entries: root,
    };

    for (let ii = 0; ii < pathSegments.length; ++ii) {
      const segment = pathSegments[ii];
      const isLastSegment = ii === pathSegments.length - 1;

      if (isLastSegment) {
        currentNode.entries.push({
          name: segment,
          fullPath: file.filename,
          file: file,
          entries: [],
        });
      } else {
        let entry = currentNode.entries.find((x) => x.name === segment);

        if (!entry) {
          entry = {
            name: segment,
            fullPath: pathSegments.slice(0, ii + 1).join("/"),
            file: undefined,
            entries: [],
          };
          currentNode.entries.push(entry);
        }

        currentNode = entry;
      }
    }
  });

  return root;
}

const downloadDataFileToLocalBlob = (
  selectedDataFileEntry: SelectedVehicleFile,
) =>
  vehicleFileAPI.downloadDataFileAsBlob(
    selectedDataFileEntry.vehicleFileNumber,
    selectedDataFileEntry.fullPath,
    selectedDataFileEntry.fileType,
    false,
  );

const AsyncVehicleFilePdf = ({
  selectedVehicleFile,
  ...props
}: {
  selectedVehicleFile: SelectedVehicleFile;
  width?: number | string | undefined;
  height?: number | string | undefined;
} & HTMLAttributes<HTMLElement>) => {
  const [loadedPdf, setLoadedPdf] = useState<URL | undefined | null>(undefined);
  const pdfRef = useRef<HTMLObjectElement | null>(null);

  useEffect(() => {
    setLoadedPdf(undefined);
    const fetchUrl = async () => {
      var blob = await downloadDataFileToLocalBlob(selectedVehicleFile);
      if (blob === null) {
        return null;
      }
      const url = new URL(URL.createObjectURL(blob));
      setLoadedPdf(url);
      if (pdfRef.current !== null) {
        pdfRef.current.onload = () => {
          if (blob !== null && blob !== undefined) {
            // dispose the blob
            URL.revokeObjectURL(url.href);
          }
        };
      }
    };
    fetchUrl();
  }, [selectedVehicleFile]);

  return loadedPdf ? (
    <object
      data={loadedPdf.href}
      type="application/pdf"
      title="Embedded PDF Viewer"
      ref={pdfRef}
      {...props}
    >
      <p>
        Your browser does not support PDFs.
        <a href={loadedPdf.href}>Download the PDF</a>
      </p>
    </object>
  ) : (
    <Spinner {...props} />
  );
};

const AsyncBlobVehicleFileImage = ({
  selectedVehicleFile,
  ...props
}: { selectedVehicleFile: SelectedVehicleFile } & ImageProps &
  RefAttributes<HTMLImageElement>) => {
  const [loadedSrc, setLoadedSrc] = useState<URL | undefined | null>(undefined);
  const imageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    setLoadedSrc(undefined);
    const fetchUrl = async () => {
      var blob = await downloadDataFileToLocalBlob(selectedVehicleFile);
      if (blob === null) {
        return null;
      }
      const url = new URL(URL.createObjectURL(blob));
      setLoadedSrc(url);
      console.log("AsyncBlobVehicleFileImage " + selectedVehicleFile.fullPath);
      if (imageRef.current !== null) {
        imageRef.current.onload = () => {
          if (blob !== null && blob !== undefined) {
            // dispose the blob
            URL.revokeObjectURL(url.href);
          }
        };
      }
    };
    fetchUrl();
  }, [selectedVehicleFile]);

  return loadedSrc ? (
    <Image src={loadedSrc?.href} {...props} ref={imageRef} alt={props.alt} />
  ) : (
    // <Spinner {...props} />
    <div />
  );
};

export default VehicleFileDetailsPage;
